<template>
  <div class="admin-users-container">
    <Loading :show="loading" />

    <div :class="{ disabled: loading }">
      <h1>Användare</h1>
      <hr />

      <!-- Form to add admin -->
      <div class="add-user-section">
        <h3>Lägg till ny administratör</h3>
        <form @submit.prevent="addUser" class="add-user-form">
          <div class="form-group">
            <label for="new-email">E-postadress</label>
            <input
              type="email"
              id="new-email"
              v-model="newEmail"
              placeholder="namn@branas.se"
              required
            />
          </div>
          <button type="submit" class="button">Lägg till</button>
        </form>
      </div>

      <hr />

      <!-- List of admins -->
      <div class="users-list-section">
        <h3>Befintliga användare</h3>
        <table class="users-table">
          <thead>
            <tr>
              <th>E-post</th>
              <th>Roll</th>
              <th>Skapad</th>
              <th>Senaste inloggning</th>
              <th>Senaste aktivitet</th>
              <th>Åtgärd</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td class="email-cell">{{ user.email }}</td>
              <td>
                <span :class="'role-badge ' + user.role">{{ user.role }}</span>
              </td>
              <td>{{ formatDate(user.created_at) }}</td>
              <td>{{ formatDate(user.last_login_at) }}</td>
              <td>{{ formatDate(user.last_activity_at) }}</td>
              <td>
                <button
                  v-if="user.email !== currentUserEmail && user.role !== 'superadmin'"
                  @click="deleteUser(user)"
                  class="btn btn-delete"
                >
                  Ta bort
                </button>
                <span v-else-if="user.email === currentUserEmail" class="self-label">Du</span>
                <span v-else class="self-label">Skyddad</span>
              </td>
            </tr>
            <tr v-if="users.length === 0">
              <td colspan="6" class="empty-cell">Inga användare hittades.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/components/Loading';
import API from '@/classes/API';
import moment from 'moment';

export default {
  components: {
    Loading,
  },
  data() {
    return {
      loading: false,
      users: [],
      newEmail: '',
      newRole: 'admin',
      currentUserEmail: '',
    };
  },
  mounted() {
    this.fetchCurrentUser();
    this.fetchUsers();
  },
  methods: {
    async fetchCurrentUser() {
      try {
        const { user } = await API.get('/admin/user');
        if (user && user.email) {
          this.currentUserEmail = user.email;
        }
      } catch (e) {
        // Safe fallback
      }
    },
    formatDate(date) {
      if (!date) {
        return '-';
      }
      return moment(date).format('YYYY-MM-DD HH:mm:ss');
    },
    async fetchUsers() {
      this.loading = true;
      try {
        const response = await API.get('/admin/users');
        if (response && response.success) {
          this.users = response.users || [];
        } else {
          this.$toast.error((response && response.errorMessage) || 'Det gick inte att hämta användare.', 'Fel', {
            position: 'topRight',
          });
        }
      } catch (error) {
        const errMsg = error.response && error.response.data && error.response.data.errorMessage
          ? error.response.data.errorMessage
          : 'Ett oväntat fel uppstod.';
        this.$toast.error(errMsg, 'Fel', {
          position: 'topRight',
        });
      } finally {
        this.loading = false;
      }
    },
    async addUser() {
      if (!this.newEmail) {
        return;
      }

      this.loading = true;
      try {
        const response = await API.post('/admin/users', {
          email: this.newEmail,
        });

        if (response && response.success) {
          this.$toast.success('Användaren har lagts till.', 'Sparat', {
            position: 'topRight',
          });
          this.newEmail = '';
          this.newRole = 'admin';
          await this.fetchUsers();
        } else {
          this.$toast.error((response && response.errorMessage) || 'Det gick inte att lägga till användaren.', 'Fel', {
            position: 'topRight',
          });
        }
      } catch (error) {
        const errMsg = error.response && error.response.data && error.response.data.errorMessage
          ? error.response.data.errorMessage
          : 'Ett oväntat fel uppstod.';
        this.$toast.error(errMsg, 'Fel', {
          position: 'topRight',
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteUser(user) {
      const confirmDelete = window.confirm(`Är du säker på att du vill ta bort administratören ${user.email}?`);
      if (!confirmDelete) {
        return;
      }

      this.loading = true;
      try {
        const response = await API.delete(`/admin/users/${user.id}`);
        if (response && response.success) {
          this.$toast.success('Användaren har tagits bort.', 'Borttagen', {
            position: 'topRight',
          });
          await this.fetchUsers();
        } else {
          this.$toast.error((response && response.errorMessage) || 'Det gick inte att ta bort användaren.', 'Fel', {
            position: 'topRight',
          });
        }
      } catch (error) {
        const errMsg = error.response && error.response.data && error.response.data.errorMessage
          ? error.response.data.errorMessage
          : 'Ett oväntat fel uppstod.';
        this.$toast.error(errMsg, 'Fel', {
          position: 'topRight',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="stylus">
@import '~@/global.styl'

.admin-users-container
  admin-container()
  max-width 1000px
  margin 0 auto
  padding 20px

.disabled
  opacity 0.5
  pointer-events none
  filter grayscale(100%)

.add-user-section
  background #f9f9f9
  padding 20px
  border-radius 4px
  border 1px solid #e3e3e3
  margin-bottom 20px

.add-user-form
  display flex
  flex-wrap wrap
  align-items flex-end
  gap 15px

.form-group
  display flex
  flex-direction column
  gap 5px
  flex 1
  min-width 200px
  label
    font-size 0.9em
    font-weight bold
    color #555
  input, select
    padding 8px 12px
    border 1px solid #ccc
    border-radius 4px
    font-size 1em
    width 100%
    box-sizing border-box

.btn
  padding 9px 20px
  border none
  border-radius 4px
  cursor pointer
  font-weight bold
  font-size 0.95em
  transition background 0.2s

.btn-add
  background #28a745
  color #fff
  &:hover
    background #218838

.btn-delete
  background #dc3545
  color #fff
  padding 5px 10px
  font-size 0.85em
  &:hover
    background #c82333

.self-label
  color #777
  font-style italic

.users-table
  width 100%
  border-collapse collapse
  margin-top 15px

th, td
  text-align left
  padding 10px 12px
  border-bottom 1px solid #ddd

th
  font-weight bold
  background #f1f1f1

.email-cell
  font-weight 500

.role-badge
  padding 3px 8px
  border-radius 12px
  font-size 0.85em
  font-weight bold
  text-transform uppercase
  &.admin
    background #e2f0fe
    color #0066cc
  &.translator
    background #fff3cd
    color #856404
  &.superadmin
    background #d4edda
    color #155724

.empty-cell
  text-align center
  padding 30px
  color #777
</style>



// WEBPACK FOOTER //
// Users.vue?54d73478